import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonSelect, IonSelectOption, IonTitle, IonToolbar, isPlatform } from '@ionic/react';
import { addOutline, arrowBack, trashOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';

type Conexao = {
    tipo: 'local' | 'rede' | 'ble' | 'spp';
    localName?: string;
    redeIp?: string;
    redePorta?: string;
    bluetoothDeviceId?: string;
    bluetoothSPPAddress?: string;
};

type Impressora = {
    nome: string;
    conexoes: Conexao[];
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (impressoras: Impressora[]) => void;
    impressorasIniciais: Impressora[];
    impressorasLocal?: any[];
    onConfigurarBluetooth: (impressoraIndex: number, conexaoIndex: number, tipo: 'ble' | 'spp') => void;
    onTestarImpressao: (conexao: Conexao) => void;
};

const ModalMultiImpressao: React.FC<Props> = ({ isOpen, onClose, onSave, impressorasIniciais, impressorasLocal, onConfigurarBluetooth, onTestarImpressao }) => {
    const [impressoras, setImpressoras] = useState<Impressora[]>([]);
    const isMobile = !isPlatform('mobileweb') && isPlatform('mobile');
    const isAndroid = isPlatform('android');

    useEffect(() => {
        if (isOpen) {
            // Copia as impressoras iniciais ao abrir o modal
            setImpressoras(impressorasIniciais.length > 0 ? JSON.parse(JSON.stringify(impressorasIniciais)) : []);
        }
    }, [isOpen, impressorasIniciais]);

    const adicionarImpressora = () => {
        const novoNumero = impressoras.length + 2; // +2 porque começa em "Impressora 2"
        const novaImpressora: Impressora = {
            nome: `Impressora ${novoNumero}`,
            conexoes: []
        };
        setImpressoras([...impressoras, novaImpressora]);
    };

    const removerImpressora = (impressoraIndex: number) => {
        const novasImpressoras = impressoras.filter((_, i) => i !== impressoraIndex);
        setImpressoras(novasImpressoras);
    };

    const adicionarConexao = (impressoraIndex: number) => {
        const novasImpressoras = [...impressoras];
        const tipoDefault = isMobile ? 'rede' : 'local';

        const novaConexao: Conexao = {
            tipo: tipoDefault as any,
            localName: '',
            redeIp: '',
            redePorta: '',
            bluetoothDeviceId: '',
            bluetoothSPPAddress: '',
        };

        novasImpressoras[impressoraIndex].conexoes.push(novaConexao);
        setImpressoras(novasImpressoras);
    };

    const removerConexao = (impressoraIndex: number, conexaoIndex: number) => {
        const novasImpressoras = [...impressoras];
        novasImpressoras[impressoraIndex].conexoes = novasImpressoras[impressoraIndex].conexoes.filter((_, i) => i !== conexaoIndex);
        setImpressoras(novasImpressoras);
    };

    const alterarTipoConexao = (impressoraIndex: number, conexaoIndex: number, novoTipo: string) => {
        const novasImpressoras = [...impressoras];
        const tipoAtual = novasImpressoras[impressoraIndex].conexoes[conexaoIndex].tipo;

        // Limpa todos os campos ao mudar o tipo
        novasImpressoras[impressoraIndex].conexoes[conexaoIndex] = {
            tipo: novoTipo as any,
            localName: '',
            redeIp: '',
            redePorta: '',
            bluetoothDeviceId: '',
            bluetoothSPPAddress: '',
        };

        setImpressoras(novasImpressoras);
    };

    const alterarCampoConexao = (impressoraIndex: number, conexaoIndex: number, campo: string, valor: string) => {
        const novasImpressoras = [...impressoras];
        (novasImpressoras[impressoraIndex].conexoes[conexaoIndex] as any)[campo] = valor;
        setImpressoras(novasImpressoras);
    };

    const alterarNomeImpressora = (impressoraIndex: number, novoNome: string) => {
        const novasImpressoras = [...impressoras];
        novasImpressoras[impressoraIndex].nome = novoNome;
        setImpressoras(novasImpressoras);
    };

    const handleSalvar = () => {
        // Remove impressoras sem conexões
        const impressorasValidas = impressoras.filter(imp => imp.conexoes.length > 0);
        onSave(impressorasValidas);
        onClose();
    };

    const getConfigAtual = (conexao: Conexao) => {
        if (conexao.tipo === 'local' && conexao.localName) {
            return conexao.localName;
        } else if (conexao.tipo === 'rede' && conexao.redeIp) {
            return `${conexao.redeIp}:${conexao.redePorta || '9100'}`;
        } else if (conexao.tipo === 'ble' && conexao.bluetoothDeviceId) {
            return 'Bluetooth BLE configurado';
        } else if (conexao.tipo === 'spp' && conexao.bluetoothSPPAddress) {
            return 'Bluetooth SPP configurado';
        }
        return 'Não configurado';
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose}>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonButtons slot='start'>
                        <IonButton onClick={onClose}>
                            <IonIcon slot="icon-only" icon={arrowBack} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>Configurar Multi Impressão</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent>
                <IonList>
                    <IonItem lines="none">
                        <p style={{ fontSize: '0.9em', color: '#666' }}>
                            Configure impressoras adicionais. Cada impressora pode ter múltiplas conexões (Desktop e Mobile).
                        </p>
                    </IonItem>

                    {impressoras.map((impressora, impressoraIndex) => (
                        <div key={impressoraIndex} style={{ marginBottom: '25px', padding: '15px' }}>
                            {/* Cabeçalho da Impressora */}

                            <IonLabel position="stacked" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                Nome da Impressora
                            </IonLabel>
                            <div style={{ borderRadius: "5px", width: "100%", display: 'flex', alignItems: 'center' }}>
                                <IonInput
                                    value={impressora.nome}
                                    placeholder="Ex: Impressora 2"
                                    onIonInput={(e: any) => alterarNomeImpressora(impressoraIndex, e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <IonButton fill="clear" color="danger" onClick={() => removerImpressora(impressoraIndex)}>
                                    <IonIcon slot="icon-only" icon={trashOutline} />
                                </IonButton>
                            </div>


                            {/* Lista de Conexões */}
                            <IonLabel style={{ fontSize: '0.9em', fontWeight: 'bold', color: '#555', marginTop: '15px', display: 'block' }}>
                                Conexões ({impressora.conexoes.length})
                            </IonLabel>

                            {impressora.conexoes.map((conexao, conexaoIndex) => (
                                <div key={conexaoIndex} style={{ marginTop: '10px', padding: '10px', background: '#f9f9f9', borderRadius: '5px', border: '1px solid #e0e0e0' }}>
                                    <IonItem lines="none" style={{ marginBottom: '5px' }}>
                                        <IonLabel position="stacked">Tipo de Conexão</IonLabel>
                                        <IonSelect
                                            value={conexao.tipo}
                                            placeholder="Selecione o tipo"
                                            onIonChange={(e) => alterarTipoConexao(impressoraIndex, conexaoIndex, e.target.value)}
                                            style={{ width: '100%' }}
                                        >
                                            {!isMobile && (
                                                <IonSelectOption value="local">Impressora Local (Desktop)</IonSelectOption>
                                            )}
                                            {isMobile && (
                                                <>
                                                    <IonSelectOption value="rede">Impressora de Rede</IonSelectOption>
                                                    <IonSelectOption value="ble">Bluetooth BLE</IonSelectOption>
                                                    {isAndroid && (
                                                        <IonSelectOption value="spp">Bluetooth SPP (Android)</IonSelectOption>
                                                    )}
                                                </>
                                            )}
                                        </IonSelect>
                                    </IonItem>

                                    <IonItem lines="none">
                                        <IonButton fill="clear" slot='end' color="danger" size="small" onClick={() => removerConexao(impressoraIndex, conexaoIndex)}>
                                            <IonIcon slot="start" icon={trashOutline} />
                                            Remover Conexão
                                        </IonButton>
                                    </IonItem>

                                    {/* Configuração Local */}
                                    {conexao.tipo === 'local' && !isMobile && (
                                        <IonItem lines="none">
                                            <IonLabel position="stacked">Impressora Local</IonLabel>
                                            <IonSelect
                                                value={conexao.localName}
                                                placeholder="Selecione uma impressora"
                                                onIonChange={(e) => alterarCampoConexao(impressoraIndex, conexaoIndex, 'localName', e.target.value)}
                                            >
                                                {impressorasLocal?.map((imp: any, idx: any) => (
                                                    <IonSelectOption key={idx} value={imp.Name}>
                                                        {imp.Name}
                                                    </IonSelectOption>
                                                ))}
                                            </IonSelect>
                                        </IonItem>
                                    )}

                                    {/* Configuração Rede */}
                                    {conexao.tipo === 'rede' && isMobile && (
                                        <>
                                            <IonItem lines="none">
                                                <IonLabel position="stacked">IP da Impressora</IonLabel>
                                                <div style={{ background: "#eeeeee", borderRadius: "5px", padding: "10px", marginTop: "10px", width: "100%" }}>
                                                    <IonInput
                                                        type="text"
                                                        value={conexao.redeIp}
                                                        maxlength={15}
                                                        placeholder="IP da impressora"
                                                        onIonInput={(e: any) => alterarCampoConexao(impressoraIndex, conexaoIndex, 'redeIp', e.target.value)}
                                                    />
                                                </div>
                                            </IonItem>

                                            <IonItem lines="none">
                                                <IonLabel position="stacked">Porta</IonLabel>
                                                <div style={{ background: "#eeeeee", borderRadius: "5px", padding: "10px", marginTop: "10px", width: "100%" }}>
                                                    <IonInput
                                                        type="text"
                                                        value={conexao.redePorta}
                                                        maxlength={15}
                                                        placeholder="Porta da impressora"
                                                        onIonInput={(e: any) => alterarCampoConexao(impressoraIndex, conexaoIndex, 'redePorta', e.target.value)}
                                                    />
                                                </div>
                                            </IonItem>
                                        </>
                                    )}

                                    {/* Configuração BLE */}
                                    {conexao.tipo === 'ble' && isMobile && (
                                        <>
                                            <IonItem lines="none">
                                                <IonLabel position="stacked">Bluetooth BLE</IonLabel>
                                                <div style={{ background: "#eeeeee", borderRadius: "5px", padding: "10px", marginTop: "10px", width: "100%" }}>
                                                    <p style={{ margin: 0, color: conexao.bluetoothDeviceId ? '#333' : '#999' }}>
                                                        {conexao.bluetoothDeviceId ? 'Dispositivo configurado' : 'Nenhum dispositivo configurado'}
                                                    </p>
                                                </div>

                                            </IonItem>
                                            <IonItem lines="none">
                                                <IonButton size="default" slot="end" onClick={() => onConfigurarBluetooth(impressoraIndex, conexaoIndex, 'ble')}>
                                                    Configurar
                                                </IonButton>
                                            </IonItem>
                                        </>
                                    )}

                                    {/* Configuração SPP */}
                                    {conexao.tipo === 'spp' && isAndroid && (
                                        <>
                                            <IonItem lines="none">
                                                <IonLabel position="stacked">Bluetooth SPP</IonLabel>
                                                <div style={{ background: "#eeeeee", borderRadius: "5px", padding: "10px", marginTop: "10px", width: "100%" }}>
                                                    <p style={{ margin: 0, color: conexao.bluetoothSPPAddress ? '#333' : '#999' }}>
                                                        {conexao.bluetoothSPPAddress ? 'Dispositivo SPP configurado' : 'Nenhum dispositivo SPP configurado'}
                                                    </p>
                                                </div>

                                            </IonItem>
                                            <IonItem lines="none">
                                                <IonButton size="default" slot="end" onClick={() => onConfigurarBluetooth(impressoraIndex, conexaoIndex, 'spp')}>
                                                    Configurar
                                                </IonButton>
                                            </IonItem>
                                        </>
                                    )}

                                    {/* Status e Teste */}
                                    {/* <IonItem lines="none">
                                        <IonLabel>
                                            <p style={{ fontSize: '0.85em', color: '#666' }}>
                                                <strong>Status:</strong> {getConfigAtual(conexao)}
                                            </p>
                                        </IonLabel>
                                    </IonItem> */}

                                    {/* Botão Testar */}


                                    {((conexao.tipo === 'local' && conexao.localName) ||
                                        (conexao.tipo === 'rede' && conexao.redeIp) ||
                                        (conexao.tipo === 'ble' && conexao.bluetoothDeviceId) ||
                                        (conexao.tipo === 'spp' && conexao.bluetoothSPPAddress)) && (
                                            <IonItem lines="none">
                                                <IonButton size="default" slot="end" onClick={() => onTestarImpressao(conexao)}>
                                                    Testar Conexão
                                                </IonButton>
                                            </IonItem>
                                        )}
                                </div>
                            ))}

                            {/* Botão Adicionar Conexão */}
                            <IonItem lines="none" style={{ marginTop: '10px' }}>
                                <IonButton size="large" fill="outline" onClick={() => adicionarConexao(impressoraIndex)}>
                                    <IonIcon slot="start" icon={addOutline} />
                                    Adicionar Conexão
                                </IonButton>
                            </IonItem>
                        </div>
                    ))}

                    {/* Botão Adicionar Impressora */}
                    <IonItem lines="none">
                        <IonButton size="large" onClick={adicionarImpressora}>
                            <IonIcon slot="start" icon={addOutline} />
                            Adicionar Impressora
                        </IonButton>
                    </IonItem>
                </IonList>
            </IonContent>

            <IonToolbar>
                <IonButton size="large" expand="block" onClick={handleSalvar}>
                    Confirmar
                </IonButton>
            </IonToolbar>
        </IonModal>
    );
};

export default ModalMultiImpressao;
